import classNames from "classnames";
import React, { type JSX, useEffect, useRef } from "react";
import { t } from "ttag";

import { reverse } from "@reactivated";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { ErrorBoundary } from "../../../common/ErrorBoundary";
import { Link } from "../../../common/Link";
import { LoadingSpinner } from "../../../common/LoadingSpinner";
import { FinancingPlan } from "../../../models/financing";
import { IProductSKU, isoWebPageURL } from "../../../models/nominals";
import {
    trackBasketEvent,
    trackProductValuePageView,
} from "../../../utils/analytics";
import { usePageSetting } from "../../../utils/settings";
import { usePageURL } from "../../../utils/urls";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { TGetProductUpsell } from "../BasketLine";
import { BasketLines } from "../BasketLines";
import { BasketSummary } from "../BasketSummary";
import { WishListLines } from "../WishListLines";
import { BasketHeader } from "../components/BasketHeader";
import { BasketVoucherForm } from "../components/BasketVoucherForm";
import {
    BasketLineVariant,
    LIST_FAVORITES,
    LIST_PREVIOUSLY_IN_BASKET,
    PredictedDeliveryDateDisplayType,
    PreferredDeliveryDateDisplayType,
} from "../constants";
import { defaults } from "../defaults";
import { acknowledgeMergedBasket } from "../reducers";
import { IReduxAPIData, IReduxState } from "../reducers.interfaces";
import {
    addBasketLine,
    addVoucherCode,
    loadBasketIfOutdated,
    loadConcreteBundles,
    loadFavorites,
    removeBasketLine,
    removeVoucherCode,
    removeWishlistLine,
    updateBasketLineQuantity,
} from "../thunks";

import styles from "./Basket.module.scss";

interface BasketProps {
    getLineProductUpsell: TGetProductUpsell;
    disableFreeShippingMessage?: boolean;
    showValueProps?: boolean;
    predictedDeliveryDates?: PredictedDeliveryDateDisplayType;
    preferredDeliveryDates?: PreferredDeliveryDateDisplayType;
    getExtraSummaryContent: () => JSX.Element | null;
    getExtraSidebarContent: (
        data: IReduxAPIData,
        isMobile: boolean,
    ) => JSX.Element | null;
    getExtraMainColumnContent: (
        data: IReduxAPIData,
        isMobile: boolean,
    ) => JSX.Element | null;
    getNavHeight?: number;
    deliveryIsFree?: boolean;
}

// Constants
const TABLET_WIDTH_THRESHOLD = 769;
const MOBILE_WIDTH_THRESHOLD = 501;
const BASKET_CHECK_INTERVAL = 30000; // 30 seconds

// Helper functions
const isTabletOrSmallerWidth = () => {
    const window = getWindow();
    if (!window) {
        return false;
    }
    return window.innerWidth < TABLET_WIDTH_THRESHOLD;
};
const isMobileWidth = () => {
    const window = getWindow();
    if (!window) {
        return false;
    }
    return window.innerWidth < MOBILE_WIDTH_THRESHOLD;
};

const getItemsLabel = (qty: number) => {
    // TODO(i18n): Use ngettext for plurals: ngettext(msgid`item`, `items`, qty)
    return qty === 1 ? "item" : "items";
};

const trackPageView = (basket: IReduxAPIData["basket"]) => {
    if (basket) {
        const skus = basket.lines.reduce<IProductSKU[]>(
            (skuList, line) => skuList.concat(line.product.skus),
            [],
        );
        const price = basket.total_excl_tax;
        trackProductValuePageView(skus, price, "cart", "CartPageView");
        trackBasketEvent("view_cart", basket);
    }
};

const buildMultiCartMergedMessage = (
    basket: IReduxAPIData["basket"],
    acknowledgedMergedBaskets: readonly number[],
) => {
    const mergedBasketIDs = basket ? basket.merged_from : [];
    const unacknowledgedMergedBaskets = mergedBasketIDs.filter(
        (basketID) => acknowledgedMergedBaskets.indexOf(basketID) === -1,
    );

    if (unacknowledgedMergedBaskets.length <= 0) {
        return null;
    }

    return (
        <div className="basket-multicart-alert">
            <div>{t`Important messages about items in your cart.`}</div>
            <div className="basket-multicart-alert__copy basket-multicart-alert__copy--bold">
                {t`We found another cart associated with your email address.`}
            </div>
            <div>
                {t`The carts have been merged for your convenience. Please review your cart carefully before proceeding to checkout.`}
            </div>
        </div>
    );
};

const buildCheckoutButton = (isMobile: boolean) => {
    const checkoutURL = isoWebPageURL.wrap(reverse("checkout:index"));
    const classes = classNames({
        "basket-summary__checkout": true,
        "basket-summary__checkout--mobile": isMobile,
        "al-basket-summary__checkout": true,
    });
    return (
        <div className={classes}>
            <Link
                href={checkoutURL}
                target="_top"
                className="button button--full-width"
                id="basket-summary__checkout--shop-tracking"
            >
                {t`Checkout`}
            </Link>
        </div>
    );
};

const buildCurrentBasket = (
    data: IReduxAPIData,
    form: IReduxState["form"],
    dispatch: ReturnType<typeof useAppDispatch>,
    getLineProductUpsell: TGetProductUpsell,
    showValueProps: boolean,
    predictedDeliveryDates: PredictedDeliveryDateDisplayType,
    preferredDeliveryDates: PreferredDeliveryDateDisplayType,
    deliveryIsFree: boolean | undefined,
    phoneLink: string,
    phoneNumber: string,
    isMobile: boolean,
) => {
    let totalItems = 0;
    if (data.basket) {
        totalItems = data.basket.lines
            .filter((line) => !line.parent)
            .reduce((memo, line) => memo + line.quantity, 0);
    }
    let checkoutButton: JSX.Element | null = null;
    if (isMobile && totalItems > 0) {
        checkoutButton = buildCheckoutButton(isMobile);
    }
    return (
        <section>
            <BasketHeader
                hideBasketID={!data.basket}
                phoneLink={phoneLink}
                phoneNumber={phoneNumber}
                basketID={data.basket?.encoded_basket_id}
                isMobile={isTabletOrSmallerWidth()}
                totalItems={totalItems}
            />
            {checkoutButton}
            <BasketLines
                financingPlans={
                    data.financing_plans as readonly FinancingPlan[]
                }
                basket={data.basket}
                shippingMethodCode={form.shipping_method}
                getProductUpsell={getLineProductUpsell}
                showValueProps={showValueProps}
                predictedDeliveryDates={predictedDeliveryDates}
                preferredDeliveryDates={preferredDeliveryDates}
                loadConcreteBundles={(productURL) => {
                    dispatch(loadConcreteBundles({ productURL }));
                }}
                addBasketLine={(productURL, qty) => {
                    return dispatch(
                        addBasketLine({ productURL, qty }),
                    ).unwrap();
                }}
                updateBasketLineQuantity={(lineURL, newQty) => {
                    return dispatch(
                        updateBasketLineQuantity({ lineURL, newQty }),
                    ).unwrap();
                }}
                removeBasketLine={(lineURL) => {
                    return dispatch(removeBasketLine({ lineURL })).unwrap();
                }}
                variant={BasketLineVariant.FULL}
                isMobile={isMobileWidth()}
                deliveryIsFree={deliveryIsFree}
                removeVoucherCode={(code) => {
                    dispatch(removeVoucherCode({ code }));
                }}
            />
        </section>
    );
};

const buildFavorites = (
    wishlists: IReduxAPIData["wishlists"],
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    const lines = wishlists[LIST_FAVORITES];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!lines) {
        return null;
    }
    return (
        <div className="basket-lines__block basket-lines__block--favorites">
            <div className="basket-lines__header">
                <h1 className="basket-lines__header-title">
                    {t`Your Favorites`}
                </h1>
            </div>
            <WishListLines
                lines={lines}
                removeWishlistLine={(lineURL) => {
                    return dispatch(removeWishlistLine({ lineURL })).unwrap();
                }}
            />
        </div>
    );
};

const buildProductsPreviouslyInBasket = (
    wishlists: IReduxAPIData["wishlists"],
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    const lines = wishlists[LIST_PREVIOUSLY_IN_BASKET];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!lines || !lines.length) {
        return null;
    }
    return (
        <div className="basket-lines__block basket-lines__block--previously-in-basket">
            <h2 className="basket-lines__header">
                {/* TODO(i18n): Use jt to keep sentence together with interpolated component instead of splitting: jt`Previously in Cart (${lines.length} ${itemsLabel})` */}
                {t`Previously in Cart`}{" "}
                <span className="basket-lines__header__count">
                    ({lines.length} {getItemsLabel(lines.length)})
                </span>
            </h2>
            <WishListLines
                lines={lines}
                addBasketLine={(productURL, qty) => {
                    return dispatch(
                        addBasketLine({ productURL, qty }),
                    ).unwrap();
                }}
                removeWishlistLine={(lineURL) => {
                    return dispatch(removeWishlistLine({ lineURL })).unwrap();
                }}
            />
        </div>
    );
};

const renderAuxBasketContent = () => {
    const document = getDocument();
    if (!document) {
        return null;
    }

    const elem = document.getElementById("aux-basket-content");
    const content = {
        __html: elem ? elem.innerHTML : "",
    };

    return (
        <div
            className="aux-basket-content"
            dangerouslySetInnerHTML={content}
        ></div>
    );
};

export const Basket = (props: BasketProps): React.ReactNode => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const state = useAppSelector((rootState) => rootState.checkout || defaults);
    const dispatch = useAppDispatch();

    const homeURL = usePageURL("home");
    const enableEcom = usePageSetting("enable_ecom");
    const supportPhoneNumberLink =
        usePageSetting("support_website_phone_number_link") || "";
    const supportPhoneNumberDisplay =
        usePageSetting("support_website_phone_number_display") || "";

    const hasTrackedPageView = useRef(false);
    const pollingTimeoutRef = useRef<number | null>(null);

    // Setup polling mechanism
    useEffect(() => {
        const window = getWindow();
        if (!window) {
            return;
        }

        const startPolling = () => {
            void dispatch(
                loadBasketIfOutdated({ currentBasket: state.data.basket }),
            ).then(() => {
                pollingTimeoutRef.current = window.setTimeout(
                    startPolling,
                    BASKET_CHECK_INTERVAL,
                );
            });
        };

        // Start the first poll after the initial interval
        pollingTimeoutRef.current = window.setTimeout(
            startPolling,
            BASKET_CHECK_INTERVAL,
        );

        return () => {
            if (pollingTimeoutRef.current !== null) {
                clearTimeout(pollingTimeoutRef.current);
            }
        };
    }, [dispatch, state.data.basket]);

    // Initial data load
    useEffect(() => {
        const loadInitialData = async () => {
            const { isOutdated } = await dispatch(
                loadBasketIfOutdated({ currentBasket: state.data.basket }),
            ).unwrap();
            void dispatch(loadFavorites());

            // Track page view if basket is not outdated
            if (
                state.data.basket &&
                !isOutdated &&
                !hasTrackedPageView.current
            ) {
                trackPageView(state.data.basket);
                hasTrackedPageView.current = true;
            }
        };

        void loadInitialData();
    }, [dispatch]);

    // Track page view when basket changes
    useEffect(() => {
        if (state.data.basket) {
            trackPageView(state.data.basket);
        }
    }, [state.data.basket?.total_excl_tax]);

    // Setup beforeunload handler
    useEffect(() => {
        const window = getWindow();
        if (!window) {
            return;
        }

        const handleBeforeUnload = () => {
            if (state.data.basket) {
                state.data.basket.merged_from.forEach((mergedBasketID) => {
                    dispatch(acknowledgeMergedBasket(mergedBasketID));
                });
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [state.data.basket, dispatch]);

    if (!state.data.basket) {
        return <LoadingSpinner />;
    }

    const isMobile = isTabletOrSmallerWidth();
    const predictedDeliveryDates =
        props.predictedDeliveryDates ||
        PredictedDeliveryDateDisplayType.DISABLED;
    const preferredDeliveryDates =
        props.preferredDeliveryDates ||
        PreferredDeliveryDateDisplayType.DISABLED;

    let checkoutBtn: JSX.Element | null = null;
    if (state.data.basket.lines.length > 0) {
        checkoutBtn = (
            <div className="basket-summary__buttons">
                {buildCheckoutButton(isMobile)}
                <div className="basket-summary__continue-shopping al-basket__continue-shopping">
                    <Link
                        href={homeURL}
                        target="_top"
                        className="button button--full-width button--inverse"
                    >
                        {t`Continue Shopping`}
                    </Link>
                </div>
            </div>
        );
    }

    const mainContent = enableEcom ? (
        <>
            {buildMultiCartMergedMessage(
                state.data.basket,
                state.derived.acknowledged_merged_baskets,
            )}
            {buildCurrentBasket(
                state.data,
                state.form,
                dispatch,
                props.getLineProductUpsell,
                !!props.showValueProps,
                predictedDeliveryDates,
                preferredDeliveryDates,
                props.deliveryIsFree,
                supportPhoneNumberLink,
                supportPhoneNumberDisplay,
                isMobile,
            )}
            {buildProductsPreviouslyInBasket(state.data.wishlists, dispatch)}
        </>
    ) : (
        <>{buildFavorites(state.data.wishlists, dispatch)}</>
    );

    const extraContent = props.getExtraMainColumnContent
        ? props.getExtraMainColumnContent(state.data, isMobile)
        : null;

    const extraSidebarContent = props.getExtraSidebarContent
        ? props.getExtraSidebarContent(state.data, isMobile)
        : null;

    return (
        <div className={`${styles.root} basket l-capped-width`}>
            <div className="basket-lines">
                {mainContent}
                {renderAuxBasketContent()}
                <ErrorBoundary>{extraContent}</ErrorBoundary>
            </div>
            <div className="basket-summary">
                <div className={styles.stickyContainer}>
                    {enableEcom && (
                        <>
                            <h2 className="basket-summary__header">
                                {t`Order Summary`}
                            </h2>
                            <BasketSummary
                                data={state.data}
                                derived={state.derived}
                                disableShipping={true}
                                disableFreeShippingMessage={
                                    props.disableFreeShippingMessage
                                }
                                disableTaxes={true}
                                removeVoucherCode={(code) => {
                                    dispatch(removeVoucherCode({ code }));
                                }}
                                getExtraContent={props.getExtraSummaryContent}
                            />
                            <div className="basket-summary__promocode">
                                <BasketVoucherForm
                                    addVoucherCode={(code) => {
                                        return dispatch(
                                            addVoucherCode({ code }),
                                        ).unwrap();
                                    }}
                                    vouchers={state.data.basket?.vouchers || []}
                                />
                            </div>
                            {checkoutBtn}
                        </>
                    )}
                    <ErrorBoundary>{extraSidebarContent}</ErrorBoundary>
                </div>
            </div>
        </div>
    );
};
